import React, { useEffect, useMemo, useRef, useState } from 'react'
import '../Reports.scss'
import { useCoreContext } from 'spark-core-dx/contexts'
import { useCustomManager } from '../../../hooks/useManagers'

const DEFAULT_LOCAL_VIEWER_URL = 'https://localhost:44344';
const DEFAULT_REPORT_ID = '160855cf-089e-40e8-85cf-7318a91c822f';

export const ReportingIFrame = ({ reportId }) => {
    const { ReportViewerUrl } = useCoreContext();
    const reportMgr = useCustomManager("Report");
    const iframeRef = useRef(null);

    const [viewerUrl, setViewerUrl] = useState(null);
    const [handoffId, setHandoffId] = useState(null);
    const [frameLoaded, setFrameLoaded] = useState(false);
    const [loadError, setLoadError] = useState(null);

    const resolvedReportId = reportId ?? DEFAULT_REPORT_ID;
    const fallbackViewerUrl = ReportViewerUrl ?? DEFAULT_LOCAL_VIEWER_URL;
    const viewerOrigin = useMemo(() => {
        try {
            return viewerUrl ? new URL(viewerUrl).origin : null;
        } catch {
            return null;
        }
    }, [viewerUrl]);

    useEffect(() => {
        let cancelled = false;

        setViewerUrl(null);
        setHandoffId(null);
        setFrameLoaded(false);
        setLoadError(null);

        if (!reportMgr || !resolvedReportId) {
            return undefined;
        }

        reportMgr.issueViewerHandoff(resolvedReportId).then((result) => {
            if (cancelled) {
                return;
            }

            if (!result?.Success) {
                setLoadError(result?.Message?.DisplayMessage ?? "Unable to load the report viewer.");
                return;
            }

            const handoffValue = result?.Value ?? {};
            const nextViewerUrl = handoffValue?.ViewerUrl ?? handoffValue?.viewerUrl ?? fallbackViewerUrl;
            const nextHandoffId = handoffValue?.HandoffId ?? handoffValue?.handoffId;

            if (!nextViewerUrl || !nextHandoffId) {
                setLoadError("Unable to initialize the report viewer.");
                return;
            }

            setViewerUrl(String(nextViewerUrl).trim().replace(/\/+$/, ""));
            setHandoffId(String(nextHandoffId).trim());
        });

        return () => {
            cancelled = true;
        };
    }, [fallbackViewerUrl, reportMgr, resolvedReportId]);

    useEffect(() => {
        if (!frameLoaded || !handoffId || !viewerOrigin || !iframeRef.current?.contentWindow) {
            return;
        }

        iframeRef.current.contentWindow.postMessage(
            {
                type: "ReportViewerHandoff",
                handoffId,
            },
            viewerOrigin
        );
    }, [frameLoaded, handoffId, viewerOrigin]);

    if (loadError) {
        return <div className="message-wrapper">{loadError}</div>;
    }

    if (!viewerUrl || !handoffId) {
        return <div className="message-wrapper">Loading report viewer...</div>;
    }

    return (
        <iframe
            key={handoffId}
            ref={iframeRef}
            src={viewerUrl}
            title="Reporting"
            style={{ width: '100%', height: '100%' }}
            onLoad={() => setFrameLoaded(true)}
        ></iframe>
    );
};
